import { css } from '@emotion/react';
import { Close } from '@mui/icons-material';
import { Box, IconButton, Modal, Paper, Typography } from '@mui/material';

import { readEventAccessCodeQRCode } from '@jebel/utils';

import { useAttendEvent } from 'features/events/hooks';
import { QRCodeReader, QRCodeReaderResult } from 'shared/components/symbols';
import { useToast } from 'shared/hooks';
import { recordError } from 'shared/utils/record';

export type EventAttendanceModalProps = {
  show: boolean;

  onClose?: () => void;
};

export function EventAttendanceModal({ show, onClose }: EventAttendanceModalProps) {
  const { showSuccess, showError } = useToast();
  const { mutate: markAttendance } = useAttendEvent();

  const handleSuccessRead = async (text: string) => {
    try {
      const result = readEventAccessCodeQRCode(text);

      if (!result) {
        showError('The QR code is not valid');
        return;
      }

      const response = await markAttendance(result.event);

      if (response?.message) {
        if (response.success) {
          showSuccess(response.message);
        } else {
          showError(response.message);
        }
      }
    } catch (err) {
      showError('Something went wrong while reading the QR code');
      recordError(err);
    }
  };

  const handleDetection = (result: QRCodeReaderResult) => {
    handleSuccessRead(result.value);
  };

  const handleError = (error: Error) => {
    showError('Something went wrong while reading the QR code');
    recordError(error);
  };

  return (
    <Modal open={show} onClose={onClose} css={modalCSS}>
      <Paper css={paperCSS}>
        <Box css={headerCSS}>
          <Typography variant="h6">Scan QR Code</Typography>

          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        <QRCodeReader
          preferredCamera="environment"
          onDetect={handleDetection}
          onError={handleError}
        />
      </Paper>
    </Modal>
  );
}

const modalCSS = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const paperCSS = css`
  display: flex;
  flex-direction: column;
  padding: 1rem;
  width: 100%;
  max-width: 400px;
  margin: 1rem;
  border-radius: 8px;
  outline: none;
`;

const headerCSS = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;
